// src/components/PublicSellerPortal.jsx
// Public seller-facing document upload portal (no authentication required)

import React, { useState, useEffect, useRef } from 'react';
import {
  Clock, AlertCircle, CheckSquare, Download,
  Upload, FileText, Loader2, CheckCircle, XCircle, Shield, ExternalLink,
  ClipboardList, ChevronDown, ChevronRight, Save, Users
} from 'lucide-react';
import {
  getSellerTasks,
  uploadSellerFile,
  recordSellerUpload,
  uploadToSharePointAsSeller,
  getFileUrl,
  getSellerInfoRequest,
  submitSellerInfo,
  getSellerDdItems,
  recordSellerDdUpload,
  recordSellerDdResolution,
  sellerInviteThirdParty,
  sellerListThirdPartyInvites,
  supabase
} from '../lib/supabase';
import { extractPdfExtraFields, extractXlsxExtraFields } from '../lib/pdfExtract';
import { useDisplayColors } from '../lib/ThemeContext';
import { SurveyForm, dbToFormState, formStateToDb } from './SurveyRenderer';

// LFG Brand Colors (defaults)
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

const LFG_LOGO_URL = 'https://hyqjtpsiwbumowpljwyc.supabase.co/storage/v1/object/public/public-assets/LFGLogo.png';

// Format date as "Jan 15, 2025"
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// TaskCard component
function TaskCard({ task, uploading, uploadSuccess, fileInputRefs, handleFileUpload, handleDownload, primaryColor, compact = false }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(primaryColor, null);
  const getStatusBadge = () => {
    switch (task.status) {
      case 'Requested':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Needed</span>;
      case 'Received':
      case 'Under Review':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Received</span>;
      case 'Complete':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Complete</span>;
      default:
        return null;
    }
  };

  // Compact card for "Available to Upload" section
  if (compact) {
    return (
      <div className="bg-white rounded-lg border p-3 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-700 text-sm truncate">{task.name}</div>
            {task.files?.length > 0 && (
              <div className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
                <CheckCircle className="w-3 h-3" /> {task.files.length} uploaded
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={el => fileInputRefs.current[task.id] = el}
              type="file"
              multiple
              onChange={(e) => handleFileUpload(task.id, e)}
              className="hidden"
              id={`file-${task.id}`}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt"
            />
            <label
              htmlFor={`file-${task.id}`}
              className={`px-3 py-1.5 rounded text-white text-sm flex items-center gap-1.5 cursor-pointer hover:opacity-90 ${
                uploading[task.id] ? 'opacity-50 cursor-wait' : ''
              }`}
              style={{ backgroundColor: LFG_COLORS.gray }}
            >
              {uploading[task.id] ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
            </label>
            {uploadSuccess[task.id] === true && <CheckCircle className="w-4 h-4 text-green-500" />}
            {uploadSuccess[task.id] === false && <XCircle className="w-4 h-4 text-red-500" />}
          </div>
        </div>
      </div>
    );
  }

  // Standard card
  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-gray-800 text-sm truncate">{task.name}</span>
              {getStatusBadge()}
            </div>
            {task.due_date && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Due: {formatDate(task.due_date)}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <input
              ref={el => fileInputRefs.current[task.id] = el}
              type="file"
              multiple
              onChange={(e) => handleFileUpload(task.id, e)}
              className="hidden"
              id={`file-${task.id}`}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt"
            />
            <label
              htmlFor={`file-${task.id}`}
              className={`px-3 py-1.5 rounded-lg text-white text-sm flex items-center gap-1.5 cursor-pointer hover:opacity-90 ${
                uploading[task.id] ? 'opacity-50 cursor-wait' : ''
              }`}
              style={{ backgroundColor: primaryColor }}
            >
              {uploading[task.id] ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">{task.files?.length > 0 ? 'Add' : 'Upload'}</span>
                </>
              )}
            </label>
            {uploadSuccess[task.id] === true && (
              <CheckCircle className="w-5 h-5" style={{ color: displayGreen }} />
            )}
            {uploadSuccess[task.id] === false && (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
        </div>

        {/* Uploaded files */}
        {task.files?.length > 0 && (
          <div className="mt-2 pt-2 border-t space-y-1">
            {task.files.map(f => (
              <div key={f.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5 group">
                <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="flex-1 truncate text-gray-700">{f.original_name}</span>
                <span className="text-gray-400 flex-shrink-0">{formatDate(f.created_at)}</span>
                <button
                  onClick={() => handleDownload(f)}
                  className="text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: displayGreen }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// INFO FORM SECTION COMPONENTS
// ============================================================================

const INFO_TABS = [
  { id: 'personnel', label: 'Personnel' },
  { id: 'personnel_survey', label: 'HR Survey' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'utilities', label: 'Utilities' },
  { id: 'preneed', label: 'Preneed' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'crematory', label: 'Crematory' },
  { id: 'technology', label: 'Technology' },
];

// HR survey question list + options now live in survey_definitions and
// flow through <SurveyForm /> + <SurveyReview /> — no hardcoded options here.

const UTILITY_TYPES = ['gas', 'electric', 'water', 'telephone', 'internet', 'refuse_collection'];
const FACILITY_TYPES = ['hvac', 'plumber', 'electrician', 'snow_removal', 'lawn_landscape', 'parking_lot', 'pest_control', 'roofer', 'other'];
const TECH_CATEGORIES = ['website', 'phone_system', 'it_contact', 'domain_registrar', 'email_platform'];
const TECH_LABELS = { website: 'Website', phone_system: 'Phone System', it_contact: 'IT Contact', domain_registrar: 'Domain Registrar', email_platform: 'Email Platform' };
const TECH_URL_LABEL = { website: 'URL / Domain', phone_system: 'Provider / System', it_contact: 'Name / Company', domain_registrar: 'Registrar (e.g. GoDaddy)', email_platform: 'Platform (e.g. Google, M365)' };

function emptyPersonnel() {
  return { first_name: '', last_name: '', middle_initial: '', phone_number: '', email_address: '', hire_date: '', position: '', employment_type: 'Full Time', hours_per_year: '', hourly_rate: '', salary_rate: '', ytd_pto_accrued: '', pto_entitlement: '', has_401k: false, company_credit_card: false, phone_stipend: false, company_vehicle: false, life_insurance: false, clothing_allowance: false };
}
function emptyVehicle() {
  return { year: '', make: '', model: '', vin: '', mileage: '', loan_balance: '', kbb_value: '' };
}
function emptyPreneed() {
  return { company_name: '', contract_number: '', contact_name: '', contact_phone: '' };
}
function emptyComputer() {
  return { computer_type: 'desktop', make: '', model: '', serial_number: '' };
}
function emptySoftware() {
  return { name: '', monthly_cost: '', annual_cost: '', renewal_date: '', login_url: '', username: '' };
}

function InfoFormSection({ token, infoRequest, submittedData, onSectionSaved, primaryColor, restrictToCategory }) {
  // When restrictToCategory is set (e.g. hr_survey scope), force-select
  // that tab and don't render the tab bar at all — the user only has
  // access to one section.
  const [activeTab, setActiveTab] = useState(restrictToCategory || 'personnel');
  const visibleTabs = restrictToCategory
    ? INFO_TABS.filter(t => t.id === restrictToCategory)
    : INFO_TABS;
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({}); // { [tab]: 'saved' | 'error' }
  const submitted = infoRequest?.categories_submitted || [];

  // Local form state — initialized from submittedData
  const [personnel, setPersonnel] = useState(() => submittedData?.personnel?.length ? submittedData.personnel : [emptyPersonnel()]);
  const [vehicles, setVehicles] = useState(() => submittedData?.vehicles?.length ? submittedData.vehicles : [emptyVehicle()]);
  const [utilities, setUtilities] = useState(() => {
    const base = {};
    UTILITY_TYPES.forEach(t => base[t] = { vendor_name: '', account_number: '', contract_end: '' });
    if (submittedData?.utilities) {
      submittedData.utilities.forEach(u => { if (u.utility_type) base[u.utility_type] = u; });
    }
    return base;
  });
  const [preneed, setPreneed] = useState(() => submittedData?.preneed?.length ? submittedData.preneed : [emptyPreneed()]);
  const [facilities, setFacilities] = useState(() => {
    const base = {};
    FACILITY_TYPES.forEach(t => base[t] = { vendor_name: '', vendor_phone: '', contract_end: '' });
    if (submittedData?.facilities) {
      submittedData.facilities.forEach(f => { if (f.facility_type) base[f.facility_type] = f; });
    }
    return base;
  });
  const emptyCrematory = () => ({
    machine: '', installation_date: '', capacity: '', epa_certification: '',
    regulatory_info: '', maintenance_program: '', last_service_date: ''
  });
  const [crematory, setCrematory] = useState(() =>
    submittedData?.crematory?.length ? submittedData.crematory : [emptyCrematory()]
  );
  const [techComputers, setTechComputers] = useState(() => submittedData?.tech_computers?.length ? submittedData.tech_computers : [emptyComputer()]);
  const [techSoftware, setTechSoftware] = useState(() => submittedData?.tech_software?.length ? submittedData.tech_software : [emptySoftware()]);
  const [techOther, setTechOther] = useState(() => {
    const base = {};
    TECH_CATEGORIES.forEach(c => base[c] = { url: '', notes: '' });
    if (submittedData?.tech_other) {
      submittedData.tech_other.forEach(o => { if (o.category) base[o.category] = o; });
    }
    return base;
  });
  // Re-sync every section's form state once submittedData actually arrives.
  // submittedData is fetched asynchronously by the parent AFTER this
  // component already mounted, so the useState(() => ...) initializers
  // above almost always capture the empty-form fallback on first render —
  // without these effects, previously-submitted data never appears even
  // though it's correctly saved server-side (it was silently stuck behind
  // this stale-closure gap for every section except personnel_survey,
  // which already had its own re-hydrate effect below).
  //
  // snapshotRef tracks, per category, exactly which rows (by id for
  // arbitrary lists, by natural key for fixed-slot categories) this form
  // knew about as of the last sync. On save, comparing the current form
  // state against this snapshot tells the server which rows were
  // *explicitly* removed by the user — anything absent from the form that
  // ISN'T in this snapshot (e.g. a row auto-categorize-document inserted
  // after this form last synced) was never seen by the user and must be
  // left alone, not deleted.
  const snapshotRef = useRef({
    personnel: new Set(), vehicles: new Set(), preneed: new Set(), crematory: new Set(),
    tech_computers: new Set(), tech_software: new Set(),
    utilities: new Set(), facilities: new Set(), tech_other: new Set(),
  });
  useEffect(() => {
    setPersonnel(submittedData?.personnel?.length ? submittedData.personnel : [emptyPersonnel()]);
    snapshotRef.current.personnel = new Set((submittedData?.personnel || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.personnel]);
  useEffect(() => {
    setVehicles(submittedData?.vehicles?.length ? submittedData.vehicles : [emptyVehicle()]);
    snapshotRef.current.vehicles = new Set((submittedData?.vehicles || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.vehicles]);
  useEffect(() => {
    const base = {};
    UTILITY_TYPES.forEach(t => base[t] = { vendor_name: '', account_number: '', contract_end: '' });
    if (submittedData?.utilities) {
      submittedData.utilities.forEach(u => { if (u.utility_type) base[u.utility_type] = u; });
    }
    setUtilities(base);
    snapshotRef.current.utilities = new Set(
      (submittedData?.utilities || []).filter(u => u.vendor_name || u.account_number).map(u => u.utility_type)
    );
  }, [submittedData?.utilities]);
  useEffect(() => {
    setPreneed(submittedData?.preneed?.length ? submittedData.preneed : [emptyPreneed()]);
    snapshotRef.current.preneed = new Set((submittedData?.preneed || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.preneed]);
  useEffect(() => {
    const base = {};
    FACILITY_TYPES.forEach(t => base[t] = { vendor_name: '', vendor_phone: '', contract_end: '' });
    if (submittedData?.facilities) {
      submittedData.facilities.forEach(f => { if (f.facility_type) base[f.facility_type] = f; });
    }
    setFacilities(base);
    snapshotRef.current.facilities = new Set(
      (submittedData?.facilities || []).filter(f => f.vendor_name || f.vendor_phone).map(f => f.facility_type)
    );
  }, [submittedData?.facilities]);
  useEffect(() => {
    setCrematory(submittedData?.crematory?.length ? submittedData.crematory : [emptyCrematory()]);
    snapshotRef.current.crematory = new Set((submittedData?.crematory || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.crematory]);
  useEffect(() => {
    setTechComputers(submittedData?.tech_computers?.length ? submittedData.tech_computers : [emptyComputer()]);
    snapshotRef.current.tech_computers = new Set((submittedData?.tech_computers || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.tech_computers]);
  useEffect(() => {
    setTechSoftware(submittedData?.tech_software?.length ? submittedData.tech_software : [emptySoftware()]);
    snapshotRef.current.tech_software = new Set((submittedData?.tech_software || []).map(r => r.id).filter(Boolean));
  }, [submittedData?.tech_software]);
  useEffect(() => {
    const base = {};
    TECH_CATEGORIES.forEach(c => base[c] = { url: '', notes: '' });
    if (submittedData?.tech_other) {
      submittedData.tech_other.forEach(o => { if (o.category) base[o.category] = o; });
    }
    setTechOther(base);
    snapshotRef.current.tech_other = new Set(
      (submittedData?.tech_other || []).filter(o => o.url || o.notes).map(o => o.category)
    );
  }, [submittedData?.tech_other]);

  // Diff the current save payload against snapshotRef to find rows the
  // user explicitly removed (present at last sync, absent now). Anything
  // never in the snapshot to begin with (added by AI or another actor
  // after this form's last sync) is left out of the result and therefore
  // untouched by the server.
  const computeRemoved = (category, data) => {
    const snap = snapshotRef.current;
    if (category === 'utilities') {
      const current = new Set(data.map(r => r.utility_type));
      return Array.from(snap.utilities).filter(k => !current.has(k));
    }
    if (category === 'facilities') {
      const current = new Set(data.map(r => r.facility_type));
      return Array.from(snap.facilities).filter(k => !current.has(k));
    }
    if (category === 'technology') {
      const currentComputers = new Set((data.computers || []).map(r => r.id).filter(Boolean));
      const currentSoftware = new Set((data.software || []).map(r => r.id).filter(Boolean));
      const currentOther = new Set((data.other || []).map(r => r.category));
      return {
        computers: Array.from(snap.tech_computers).filter(id => !currentComputers.has(id)),
        software: Array.from(snap.tech_software).filter(id => !currentSoftware.has(id)),
        other: Array.from(snap.tech_other).filter(k => !currentOther.has(k)),
      };
    }
    const current = new Set((Array.isArray(data) ? data : []).map(r => r.id).filter(Boolean));
    return Array.from(snap[category] || []).filter(id => !current.has(id));
  };

  // Phase S — HR Survey. Schema-driven: the question list / order /
  // labels come from survey_definitions (loaded via RPC), state hydrates
  // through SurveyRenderer's dbToFormState helper.
  const [surveySchema, setSurveySchema] = useState(null);
  const [personnelSurvey, setPersonnelSurvey] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_survey_definition_for_token', {
        p_token: token,
        p_key: 'personnel_survey',
      });
      if (cancelled) return;
      if (data?.valid && data?.has_schema) setSurveySchema(data.schema);
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Hydrate state when both schema + saved data are ready. Re-runs if
  // the seller submits and submittedData changes upstream.
  useEffect(() => {
    if (!surveySchema) return;
    setPersonnelSurvey(dbToFormState(surveySchema, submittedData?.personnel_survey));
  }, [surveySchema, submittedData?.personnel_survey]);

  const updateSurvey = (patch) => setPersonnelSurvey(prev => ({ ...prev, ...patch }));

  const save = async (category, data) => {
    setSaving(true);
    try {
      await submitSellerInfo(token, category, data, computeRemoved(category, data));
      setSaveStatus(prev => ({ ...prev, [category]: 'saved' }));
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [category]: null })), 2500);
      if (onSectionSaved) onSectionSaved(category);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus(prev => ({ ...prev, [category]: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const SaveBtn = ({ category, data }) => (
    <button
      onClick={() => save(category, data)}
      disabled={saving}
      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
      style={{ backgroundColor: primaryColor }}
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saveStatus[category] === 'saved' ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
      {saveStatus[category] === 'saved' ? 'Saved!' : saveStatus[category] === 'error' ? 'Error — Retry' : 'Save'}
    </button>
  );

  const inputCls = "w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-300";
  const labelCls = "block text-xs text-gray-500 mb-0.5";

  return (
    <div className={restrictToCategory ? '' : 'bg-white rounded-xl shadow-sm border overflow-hidden'}>
      {/* Tab bar — hidden when restricted to a single category. */}
      {visibleTabs.length > 1 && (
        <div className="flex overflow-x-auto border-b bg-gray-50">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-current text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {submitted.includes(tab.id) && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="p-3">

        {/* ── PERSONNEL ── */}
        {activeTab === 'personnel' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">List all current employees including full-time, part-time, and contractors.</p>
            <div className="space-y-2">
              {personnel.map((p, i) => (
                <div key={i} className="border rounded-lg p-3 relative">
                  <div className="absolute top-2 right-2">
                    {personnel.length > 1 && (
                      <button onClick={() => setPersonnel(prev => prev.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-400 text-xs">Remove</button>
                    )}
                  </div>
                  {/* Row 1: Name */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    <div><label className={labelCls}>First Name</label><input className={inputCls} value={p.first_name} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Middle Initial</label><input className={inputCls} maxLength={1} value={p.middle_initial||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, middle_initial: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Last Name</label><input className={inputCls} value={p.last_name} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Hire Date (Seniority)</label><input className={inputCls} type="date" value={p.hire_date||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, hire_date: e.target.value } : x))} /></div>
                  </div>
                  {/* Row 2: Contact & Position */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    <div><label className={labelCls}>Phone Number</label><input className={inputCls} value={p.phone_number||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, phone_number: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Email Address</label><input className={inputCls} type="email" value={p.email_address||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, email_address: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Position / Title</label><input className={inputCls} value={p.position} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, position: e.target.value } : x))} /></div>
                    <div>
                      <label className={labelCls}>Employment Status</label>
                      <select className={inputCls} value={p.employment_type} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, employment_type: e.target.value } : x))}>
                        <option value="Full Time">Full Time</option>
                        <option value="Part Time">Part Time</option>
                        <option value="Contractor">Contractor</option>
                      </select>
                    </div>
                  </div>
                  {/* Row 3: Compensation & PTO */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
                    <div><label className={labelCls}>Hours Worked/Year</label><input className={inputCls} type="number" value={p.hours_per_year||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, hours_per_year: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Hourly Rate ($)</label><input className={inputCls} type="number" value={p.hourly_rate} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, hourly_rate: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Annual Salary ($)</label><input className={inputCls} type="number" value={p.salary_rate} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, salary_rate: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>YTD PTO Accrued (days)</label><input className={inputCls} type="number" value={p.ytd_pto_accrued||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, ytd_pto_accrued: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>PTO Entitlement (days)</label><input className={inputCls} type="number" value={p.pto_entitlement||''} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, pto_entitlement: e.target.value } : x))} /></div>
                  </div>
                  {/* Row 4: Benefits checkboxes */}
                  <div className="flex flex-wrap gap-4 mt-1 pt-2 border-t">
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.has_401k} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, has_401k: e.target.checked } : x))} /> 401(k)</label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.company_credit_card} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, company_credit_card: e.target.checked } : x))} /> Company Credit Card</label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.phone_stipend} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, phone_stipend: e.target.checked } : x))} /> Phone Stipend</label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.company_vehicle} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, company_vehicle: e.target.checked } : x))} /> Company Vehicle</label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.life_insurance} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, life_insurance: e.target.checked } : x))} /> Life Insurance</label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!p.clothing_allowance} onChange={e => setPersonnel(prev => prev.map((x, j) => j === i ? { ...x, clothing_allowance: e.target.checked } : x))} /> Clothing Allowance</label>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setPersonnel(prev => [...prev, emptyPersonnel()])} className="mt-3 text-sm text-blue-600 hover:underline">+ Add Employee</button>
            <div className="mt-4 flex justify-end"><SaveBtn category="personnel" data={personnel} /></div>
          </div>
        )}

        {/* ── VEHICLES ── */}
        {activeTab === 'vehicles' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">List all owned or leased vehicles including hearses, removal vehicles, and service vans.</p>
            <div className="space-y-2">
              {vehicles.map((v, i) => (
                <div key={i} className="border rounded-lg p-2 relative">
                  <div className="absolute top-2 right-2">
                    {vehicles.length > 1 && (
                      <button onClick={() => setVehicles(prev => prev.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-400 text-xs">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div><label className={labelCls}>Year</label><input className={inputCls} value={v.year} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Make</label><input className={inputCls} value={v.make} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, make: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Model</label><input className={inputCls} value={v.model} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, model: e.target.value } : x))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>VIN</label><input className={inputCls} value={v.vin} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, vin: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Mileage</label><input className={inputCls} type="number" value={v.mileage} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, mileage: e.target.value } : x))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Loan Balance ($)</label><input className={inputCls} type="number" value={v.loan_balance} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, loan_balance: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>KBB Value ($)</label><input className={inputCls} type="number" value={v.kbb_value} onChange={e => setVehicles(prev => prev.map((x, j) => j === i ? { ...x, kbb_value: e.target.value } : x))} /></div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setVehicles(prev => [...prev, emptyVehicle()])} className="mt-3 text-sm text-blue-600 hover:underline">+ Add Vehicle</button>
            <div className="mt-4 flex justify-end"><SaveBtn category="vehicles" data={vehicles} /></div>
          </div>
        )}

        {/* ── UTILITIES ── */}
        {activeTab === 'utilities' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">Enter vendor and account information for each utility.</p>
            <div className="border rounded-lg overflow-x-auto">
              <div className="min-w-[480px]">
              <div className="grid grid-cols-4 gap-0 bg-gray-50 border-b px-2 py-1">
                <div className="text-xs font-medium text-gray-500">Utility</div>
                <div className="text-xs font-medium text-gray-500">Vendor / Company</div>
                <div className="text-xs font-medium text-gray-500">Account #</div>
                <div className="text-xs font-medium text-gray-500">Contract End</div>
              </div>
              {UTILITY_TYPES.map((type, i) => (
                <div key={type} className={`grid grid-cols-4 gap-2 px-2 py-1.5 items-center ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="text-xs font-medium text-gray-600 capitalize">{type.replace('_', ' ')}</div>
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={utilities[type]?.vendor_name || ''} onChange={e => setUtilities(prev => ({ ...prev, [type]: { ...prev[type], vendor_name: e.target.value } }))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={utilities[type]?.account_number || ''} onChange={e => setUtilities(prev => ({ ...prev, [type]: { ...prev[type], account_number: e.target.value } }))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" type="date" value={utilities[type]?.contract_end || ''} onChange={e => setUtilities(prev => ({ ...prev, [type]: { ...prev[type], contract_end: e.target.value } }))} />
                </div>
              ))}
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <SaveBtn category="utilities" data={UTILITY_TYPES.map(t => ({ utility_type: t, ...utilities[t] })).filter(u => u.vendor_name || u.account_number)} />
            </div>
          </div>
        )}

        {/* ── PRENEED ── */}
        {activeTab === 'preneed' && (
          <div>
            <p className="text-sm text-gray-500 mb-2">List all preneed insurance or trust companies you have contracts with.</p>
            <div className="space-y-2">
              {preneed.map((p, i) => (
                <div key={i} className="border rounded-lg p-2 relative">
                  {preneed.length > 1 && (
                    <button onClick={() => setPreneed(prev => prev.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                  )}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>Company Name</label><input className={inputCls} value={p.company_name} onChange={e => setPreneed(prev => prev.map((x, j) => j === i ? { ...x, company_name: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Contract Number</label><input className={inputCls} value={p.contract_number} onChange={e => setPreneed(prev => prev.map((x, j) => j === i ? { ...x, contract_number: e.target.value } : x))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Contact Name</label><input className={inputCls} value={p.contact_name} onChange={e => setPreneed(prev => prev.map((x, j) => j === i ? { ...x, contact_name: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Contact Phone</label><input className={inputCls} value={p.contact_phone} onChange={e => setPreneed(prev => prev.map((x, j) => j === i ? { ...x, contact_phone: e.target.value } : x))} /></div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setPreneed(prev => [...prev, emptyPreneed()])} className="mt-3 text-sm text-blue-600 hover:underline">+ Add Preneed Company</button>
            <div className="mt-4 flex justify-end"><SaveBtn category="preneed" data={preneed} /></div>
          </div>
        )}

        {/* ── FACILITIES ── */}
        {activeTab === 'facilities' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">Enter vendor information for facility maintenance and service providers.</p>
            <div className="border rounded-lg overflow-x-auto">
              <div className="min-w-[480px]">
              <div className="grid grid-cols-4 gap-0 bg-gray-50 border-b px-2 py-1">
                <div className="text-xs font-medium text-gray-500">Service</div>
                <div className="text-xs font-medium text-gray-500">Vendor / Company</div>
                <div className="text-xs font-medium text-gray-500">Phone</div>
                <div className="text-xs font-medium text-gray-500">Contract End</div>
              </div>
              {FACILITY_TYPES.map((type, i) => (
                <div key={type} className={`grid grid-cols-4 gap-2 px-2 py-1.5 items-center ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="text-xs font-medium text-gray-600 capitalize">{type.replace(/_/g, ' ')}</div>
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={facilities[type]?.vendor_name || ''} onChange={e => setFacilities(prev => ({ ...prev, [type]: { ...prev[type], vendor_name: e.target.value } }))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={facilities[type]?.vendor_phone || ''} onChange={e => setFacilities(prev => ({ ...prev, [type]: { ...prev[type], vendor_phone: e.target.value } }))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" type="date" value={facilities[type]?.contract_end || ''} onChange={e => setFacilities(prev => ({ ...prev, [type]: { ...prev[type], contract_end: e.target.value } }))} />
                </div>
              ))}
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <SaveBtn category="facilities" data={FACILITY_TYPES.map(t => ({ facility_type: t, ...facilities[t] })).filter(f => f.vendor_name || f.vendor_phone)} />
            </div>
          </div>
        )}

        {/* ── CREMATORY ── */}
        {activeTab === 'crematory' && (
          <div>
            <p className="text-xs text-gray-500 mb-2">Provide details about each retort / cremation unit.</p>
            <div className="space-y-2">
              {crematory.map((c, i) => (
                <div key={i} className="border rounded-lg p-3 relative">
                  <div className="absolute top-2 right-2">
                    {crematory.length > 1 && (
                      <button onClick={() => setCrematory(prev => prev.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-400 text-xs">Remove</button>
                    )}
                  </div>
                  {crematory.length > 1 && <div className="text-xs font-medium text-gray-400 mb-2">Retort {i + 1}</div>}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>Machine</label><input className={inputCls} value={c.machine} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, machine: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Installation Date</label><input className={inputCls} type="date" value={c.installation_date} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, installation_date: e.target.value } : x))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>Capacity (per day)</label><input className={inputCls} type="number" value={c.capacity} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, capacity: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>EPA Certification</label><input className={inputCls} value={c.epa_certification} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, epa_certification: e.target.value } : x))} placeholder="e.g. EPA Cert #12345" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className={labelCls}>Last Service Date</label><input className={inputCls} type="date" value={c.last_service_date} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, last_service_date: e.target.value } : x))} /></div>
                    <div><label className={labelCls}>Regulatory Information</label><input className={inputCls} value={c.regulatory_info} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, regulatory_info: e.target.value } : x))} placeholder="State agency, permit numbers, etc." /></div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 mb-3">
                    <div><label className={labelCls}>Maintenance Program / Company</label><input className={inputCls} value={c.maintenance_program} onChange={e => setCrematory(prev => prev.map((x, j) => j === i ? { ...x, maintenance_program: e.target.value } : x))} placeholder="Program name or maintenance company" /></div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setCrematory(prev => [...prev, emptyCrematory()])} className="mt-3 text-sm text-blue-600 hover:underline">+ Add Retort</button>
            <div className="mt-4 flex justify-end"><SaveBtn category="crematory" data={crematory} /></div>
          </div>
        )}

        {/* ── TECHNOLOGY ── */}
        {activeTab === 'technology' && (
          <div className="space-y-6">
            {/* Computers */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Computers & Hardware</h3>
              <div className="space-y-2">
                {techComputers.map((c, i) => (
                  <div key={i} className="border rounded-lg p-2 relative">
                    {techComputers.length > 1 && (
                      <button onClick={() => setTechComputers(prev => prev.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                    )}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className={labelCls}>Type</label>
                        <select className={inputCls} value={c.computer_type} onChange={e => setTechComputers(prev => prev.map((x, j) => j === i ? { ...x, computer_type: e.target.value } : x))}>
                          <option value="desktop">Desktop</option>
                          <option value="laptop">Laptop</option>
                          <option value="tablet">Tablet</option>
                          <option value="server">Server</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div><label className={labelCls}>Serial #</label><input className={inputCls} value={c.serial_number} onChange={e => setTechComputers(prev => prev.map((x, j) => j === i ? { ...x, serial_number: e.target.value } : x))} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Make / Brand</label><input className={inputCls} value={c.make} onChange={e => setTechComputers(prev => prev.map((x, j) => j === i ? { ...x, make: e.target.value } : x))} /></div>
                      <div><label className={labelCls}>Model</label><input className={inputCls} value={c.model} onChange={e => setTechComputers(prev => prev.map((x, j) => j === i ? { ...x, model: e.target.value } : x))} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setTechComputers(prev => [...prev, emptyComputer()])} className="mt-2 text-sm text-blue-600 hover:underline">+ Add Computer</button>
            </div>

            {/* Software */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Software Subscriptions</h3>
              <div className="space-y-2">
                {techSoftware.map((s, i) => (
                  <div key={i} className="border rounded-lg p-2 relative">
                    {techSoftware.length > 1 && (
                      <button onClick={() => setTechSoftware(prev => prev.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                    )}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div><label className={labelCls}>Software Name</label><input className={inputCls} value={s.name} onChange={e => setTechSoftware(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                      <div><label className={labelCls}>Renewal Date</label><input className={inputCls} type="date" value={s.renewal_date} onChange={e => setTechSoftware(prev => prev.map((x, j) => j === i ? { ...x, renewal_date: e.target.value } : x))} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Monthly Cost ($)</label><input className={inputCls} type="number" value={s.monthly_cost} onChange={e => setTechSoftware(prev => prev.map((x, j) => j === i ? { ...x, monthly_cost: e.target.value } : x))} /></div>
                      <div><label className={labelCls}>Annual Cost ($)</label><input className={inputCls} type="number" value={s.annual_cost} onChange={e => setTechSoftware(prev => prev.map((x, j) => j === i ? { ...x, annual_cost: e.target.value } : x))} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setTechSoftware(prev => [...prev, emptySoftware()])} className="mt-2 text-sm text-blue-600 hover:underline">+ Add Software</button>
            </div>

            {/* Website & Phone */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Website &amp; Phone System</h3>
              <div className="space-y-2">
                {TECH_CATEGORIES.map(cat => (
                  <div key={cat} className="border rounded-lg p-2">
                    <div className="font-medium text-sm text-gray-600 mb-3">{TECH_LABELS[cat] || cat.replace('_', ' ')}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>{TECH_URL_LABEL[cat] || 'URL / Provider'}</label><input className={inputCls} value={techOther[cat]?.url || ''} onChange={e => setTechOther(prev => ({ ...prev, [cat]: { ...prev[cat], url: e.target.value } }))} /></div>
                      <div><label className={labelCls}>Notes</label><input className={inputCls} value={techOther[cat]?.notes || ''} onChange={e => setTechOther(prev => ({ ...prev, [cat]: { ...prev[cat], notes: e.target.value } }))} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <SaveBtn
                category="technology"
                data={{
                  computers: techComputers,
                  software: techSoftware,
                  other: TECH_CATEGORIES.map(c => ({ category: c, ...techOther[c] })).filter(o => o.url || o.notes),
                }}
              />
            </div>
          </div>
        )}

        {/* ── PERSONNEL SURVEY (HR / pay / benefits) — schema-driven via survey_definitions ── */}
        {activeTab === 'personnel_survey' && (surveySchema ? (
          <div>
            <SurveyForm schema={surveySchema} state={personnelSurvey} onPatch={updateSurvey} />
            <div className="mt-5 flex justify-end">
              <SaveBtn category="personnel_survey" data={formStateToDb(surveySchema, personnelSurvey)} />
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading survey…
          </div>
        ))}

      </div>
    </div>
  );
}

// ============================================================================
// DD CHECKLIST PANEL (Phase C)
// ============================================================================
// Portal-side renderer for DD items scoped to the current token. Groups by
// section, shows per-item upload button, pill status. Non-seller scopes
// see ONLY this panel; seller scope sees it above the task-based panels.

const DD_STATUS_PILLS = {
  not_started:  { label: 'Not started',  bg: 'bg-gray-100',   text: 'text-gray-600' },
  requested:    { label: 'Requested',    bg: 'bg-yellow-100', text: 'text-yellow-700' },
  received:     { label: 'Received',     bg: 'bg-purple-100', text: 'text-purple-700' },
  under_review: { label: 'Under review', bg: 'bg-orange-100', text: 'text-orange-700' },
  complete:     { label: 'Complete',     bg: 'bg-green-100',  text: 'text-green-700' },
  n_a:          { label: 'N/A',          bg: 'bg-gray-50',    text: 'text-gray-400' },
};

// Outstanding items sort ahead of in-progress ones, which sort ahead of
// done ones — keeps "what do you still need from me" at the top.
const DD_STATUS_RANK = { not_started: 0, requested: 0, under_review: 1, received: 1, complete: 2, n_a: 2 };

const SCOPE_LABELS_PORTAL = {
  seller: 'you',
  counsel: 'your counsel',
  accountant: 'your accountant',
  broker: 'your broker',
};

function DdChecklistPanel({
  items, scope, uploading, uploadSuccess, fileInputRefs, handleUpload, handleMarkHandled,
  primaryColor, displayPrimary, displayNavy, displayGreen,
}) {
  // Completed / N/A items collapse behind a toggle by default so the list
  // opens on what's still outstanding, not a mix of done-and-not-done.
  const [showCompleted, setShowCompleted] = useState(false);
  const doneItems = items.filter(i => i.status === 'complete' || i.status === 'n_a');
  const visibleItems = showCompleted ? items : items.filter(i => i.status !== 'complete' && i.status !== 'n_a');

  // Per-section collapse — sections start expanded; toggling one doesn't
  // affect the others.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const toggleSection = (section) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  // Group by section; within a section, outstanding items sort ahead of
  // in-progress ones (server order is preserved as the tiebreaker).
  const grouped = {};
  visibleItems.forEach(i => {
    if (!grouped[i.section]) grouped[i.section] = { section: i.section, section_sort: i.section_sort, items: [] };
    grouped[i.section].items.push(i);
  });
  const sections = Object.values(grouped)
    .sort((a, b) => a.section_sort - b.section_sort)
    .map(g => ({ ...g, items: [...g.items].sort((a, b) => (DD_STATUS_RANK[a.status] ?? 1) - (DD_STATUS_RANK[b.status] ?? 1)) }));

  // "Needed" only counts items THIS scope can act on — seller can act on
  // everything, third-party scopes only their own assigned items.
  const actionable = scope === 'seller' ? items : items.filter(i => i.assigned_to === scope);
  const needed = actionable.filter(i => i.status === 'not_started' || i.status === 'requested').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: displayNavy }}>
          <ClipboardList className="w-4 h-4" style={{ color: displayPrimary }} />
          Due Diligence Checklist
          <span className="text-xs font-normal text-gray-500">({items.length} item{items.length !== 1 ? 's' : ''})</span>
        </h2>
        {needed > 0 && (
          <span className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full px-2 py-0.5">
            {needed} to handle
          </span>
        )}
      </div>
      {scope === 'seller' && items.some(i => i.assigned_to !== 'seller') && (
        <p className="text-xs text-gray-500 mb-2">
          Items routed to your counsel, accountant, or broker are shown so you can track progress.
          You'll see a status update as they're completed.
        </p>
      )}
      <div className="space-y-3">
        {sections.map(group => {
          const isCollapsed = collapsedSections.has(group.section);
          return (
          <div key={group.section} className="bg-white rounded-lg border overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(group.section)}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 border-b text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                {group.section}
                <span className="font-normal text-gray-400">({group.items.length})</span>
              </span>
              {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
            </button>
            {!isCollapsed && (
            <div className="divide-y">
              {group.items.map(i => (
                <DdPortalRow
                  key={i.id}
                  item={i}
                  scope={scope}
                  uploading={uploading[i.id]}
                  uploadSuccess={uploadSuccess[i.id]}
                  fileInputRefs={fileInputRefs}
                  handleUpload={handleUpload}
                  handleMarkHandled={handleMarkHandled}
                  primaryColor={primaryColor}
                  displayGreen={displayGreen}
                />
              ))}
            </div>
            )}
          </div>
          );
        })}
        {sections.length === 0 && items.length > 0 && (
          <div className="text-xs text-gray-400 italic px-1">Nothing outstanding — everything below is complete.</div>
        )}
      </div>
      {doneItems.length > 0 && (
        <button
          type="button"
          onClick={() => setShowCompleted(v => !v)}
          className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          {showCompleted ? 'Hide' : 'Show'} {doneItems.length} completed item{doneItems.length !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

function DdPortalRow({ item, scope, uploading, uploadSuccess, fileInputRefs, handleUpload, handleMarkHandled, primaryColor, displayGreen }) {
  const pill = DD_STATUS_PILLS[item.status] || DD_STATUS_PILLS.not_started;
  const locked = item.status === 'complete' || item.status === 'n_a';
  // The seller can act on any item regardless of who it's routed to —
  // it's their business/documents ultimately, even if a counsel/
  // accountant/broker normally handles it. Third-party scopes stay
  // restricted to their own assigned items.
  const canAct = (scope === 'seller' || item.assigned_to === scope) && !locked;
  const fileCount = item.files?.length || 0;
  const showInstructions = !!item.instructions;

  return (
    <div className={`px-3 py-2.5 ${canAct ? '' : 'bg-gray-50/40'}`}>
      <div className="flex items-start gap-3">
        <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-8 text-right flex-shrink-0">{item.item_number}</span>
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${canAct ? 'text-gray-800' : 'text-gray-500'}`}>
            {item.request_text}
          </div>
          {/* Assignment hint — only shown if not acting on your own scope.
              Seller can still act on it (canAct above), this is just a
              reminder of who it's normally routed to. */}
          {scope === 'seller' && item.assigned_to !== 'seller' && !locked && (
            <div className="text-[11px] text-gray-400 mt-0.5">
              Normally handled by {SCOPE_LABELS_PORTAL[item.assigned_to] || item.assigned_to} — you can also take care of it yourself below
            </div>
          )}
          {showInstructions && canAct && (
            <div className="mt-1.5 text-xs bg-blue-50 border border-blue-100 rounded px-2 py-1.5 whitespace-pre-wrap text-gray-700">
              {item.instructions}
            </div>
          )}
          {item.resolution_note && (
            <div className="mt-1 text-xs text-gray-500 italic">Handled: {item.resolution_note}</div>
          )}
          {fileCount > 0 && (
            <div className="mt-1 space-y-0.5">
              {item.files.map(f => (
                <div key={f.id} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <FileText className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{f.original_name}</span>
                  <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: displayGreen }} />
                </div>
              ))}
            </div>
          )}
        </div>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${pill.bg} ${pill.text} flex-shrink-0`}>
          {pill.label}
        </span>
        {canAct && (
          <div className="flex flex-col gap-1 flex-shrink-0">
            <input
              ref={el => { if (el) fileInputRefs.current[item.id] = el; }}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.txt,.zip,.ppt,.pptx"
              onChange={e => handleUpload(item.id, e)}
            />
            <button
              type="button"
              onClick={() => fileInputRefs.current[item.id]?.click()}
              disabled={uploading}
              className="px-2.5 py-1 text-xs rounded flex items-center gap-1 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {uploading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : uploadSuccess === true
                  ? <CheckCircle className="w-3 h-3" />
                  : uploadSuccess === false
                    ? <XCircle className="w-3 h-3" />
                    : <Upload className="w-3 h-3" />}
              {uploading ? 'Uploading…' : uploadSuccess === true ? 'Uploaded' : uploadSuccess === false ? 'Retry' : 'Upload'}
            </button>
            <button
              type="button"
              onClick={() => handleMarkHandled(item, 'handled')}
              disabled={uploading}
              className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Mark this item handled without an upload (e.g. access granted, sent by email)"
            >
              Mark as handled
            </button>
            <button
              type="button"
              onClick={() => handleMarkHandled(item, 'na')}
              disabled={uploading}
              className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white text-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Doesn't apply to us / we don't have this"
            >
              N/A
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Without this, any uncaught render error anywhere in the portal (a bad
// data shape from AI extraction, a null where a string was expected,
// etc.) unmounts the whole React tree and leaves the seller looking at a
// totally blank white page with zero information — exactly what Luke
// reported, and there was no way for him to tell us anything more useful
// than "it's blank" since the actual error only ever showed up in a
// dev console he doesn't have open. This surfaces the real error message
// on-screen instead, so the next report is actionable.
class SellerPortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Seller portal crashed:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h1 className="text-lg font-bold text-gray-800 mb-2">Something went wrong</h1>
            <p className="text-sm text-gray-500 mb-4">
              Please screenshot this whole box (the error text especially) and send it to the team that sent you this link.
            </p>
            <pre className="text-xs text-left bg-red-50 border border-red-200 rounded p-3 overflow-auto whitespace-pre-wrap text-red-700 max-h-48">
              {this.state.error?.message || String(this.state.error)}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function PublicSellerPortal(props) {
  return (
    <SellerPortalErrorBoundary>
      <PublicSellerPortalInner {...props} />
    </SellerPortalErrorBoundary>
  );
}

function PublicSellerPortalInner({ token, orgConfig }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Self-service link renewal — offered whenever the initial load fails.
  // A garbage/mistyped token still ends up here, but request_seller_portal_otp
  // itself returns "Invalid link" for that case (it looks up the token
  // regardless of expiry, so it can't distinguish "expired" from
  // "never existed" up front) — this UI doesn't need to either.
  const [renewStep, setRenewStep] = useState('email'); // 'email' | 'code'
  const [renewEmail, setRenewEmail] = useState('');
  const [renewCode, setRenewCode] = useState('');
  const [renewSending, setRenewSending] = useState(false);
  const [renewError, setRenewError] = useState(null);
  const [dealInfo, setDealInfo] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [uploading, setUploading] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const fileInputRefs = useRef({});
  const genericInputRef = useRef(null);

  // Generic drag-and-drop upload state
  const [dragActive, setDragActive] = useState(false);
  const [genericUploads, setGenericUploads] = useState([]); // [{ key, name, size, status: 'uploading'|'done'|'error', error? }]

  // Info request state
  const [infoRequest, setInfoRequest] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);
  const [infoExpanded, setInfoExpanded] = useState(true);

  // Scope — 'seller' | 'accountant' | 'broker'. Populated from getSellerTasks
  // response. Non-seller scopes hide the Business Info panel and show a
  // context banner identifying who they're uploading as.
  const [scope, setScope] = useState('seller');
  const [scopeName, setScopeName] = useState('');

  // Phase C — DD checklist items visible to this token. Non-seller scopes
  // use the DD view instead of the task-based view. Seller scope shows both.
  const [ddItems, setDdItems] = useState([]);
  const [ddUploading, setDdUploading] = useState({});
  const [ddUploadSuccess, setDdUploadSuccess] = useState({});
  const ddFileInputRefs = useRef({});
  // Phase C.1 — mark-as-handled modal state. mode 'handled' (resolved
  // without an upload) or 'na' (doesn't apply / we don't have it).
  const [markHandledItem, setMarkHandledItem] = useState(null); // the DD item being resolved
  const [markHandledMode, setMarkHandledMode] = useState('handled');

  const primaryColor = orgConfig?.primary_color || LFG_COLORS.navy;
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(primaryColor, null);

  // Auto-bypass disclaimers for authenticated internal users
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('internal') === '1') {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setTosAccepted(true);
      });
    }
  }, []);

  useEffect(() => {
    loadSellerData();
  }, [token]);

  const loadSellerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getSellerTasks(token);
      
      if (!result.valid) {
        setError(result.error || 'Invalid or expired link');
        return;
      }
      
      setDealInfo({
        id: result.deal_id,
        name: result.deal_name
      });
      setTasks(result.tasks || []);
      setScope(result.scope || 'seller');
      setScopeName(result.scope_name || '');

      // Also load info request + previously-submitted data. submitted_data
      // is returned regardless of whether a formal request row exists —
      // don't gate on info_request being present, or already-saved
      // sections silently fail to redisplay.
      try {
        const infoResult = await getSellerInfoRequest(token);
        if (infoResult) {
          setInfoRequest(infoResult.info_request || null);
          setSubmittedData(infoResult.submitted_data || null);
        }
      } catch (infoErr) {
        // Non-critical — info request may not exist yet
        console.log('No info request found:', infoErr.message);
      }

      // Phase C — load DD items scoped to this token
      try {
        const ddResult = await getSellerDdItems(token);
        if (ddResult?.valid) {
          setDdItems(ddResult.items || []);
        }
      } catch (ddErr) {
        console.log('No DD items:', ddErr.message);
      }

    } catch (err) {
      setError(err.message || 'Failed to load portal');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (taskId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(prev => ({ ...prev, [taskId]: true }));
    setUploadSuccess(prev => ({ ...prev, [taskId]: null }));

    let anyFailed = false;
    for (const file of files) {
      try {
        const fileData = await uploadSellerFile(file, dealInfo.id, taskId);
        await recordSellerUpload(token, taskId, fileData);

        const task = tasks.find(t => t.id === taskId);
        const categoryId = task?.category_id || null;

        console.log('Starting SharePoint sync for seller upload:', file.name, 'category:', categoryId);
        uploadToSharePointAsSeller(file, token, dealInfo.name, file.name, categoryId)
          .then(result => console.log('SharePoint seller upload success:', result))
          .catch(err => console.error('SharePoint seller upload failed (non-blocking):', err));
      } catch (err) {
        console.error('Upload error:', file.name, err);
        anyFailed = true;
      }
    }

    setUploadSuccess(prev => ({ ...prev, [taskId]: !anyFailed }));
    setTimeout(() => loadSellerData(), 1000);
    setUploading(prev => ({ ...prev, [taskId]: false }));
    if (fileInputRefs.current[taskId]) {
      fileInputRefs.current[taskId].value = '';
    }
  };

  // Phase C — upload against a specific DD item. Flips item → received
  // server-side, then fires SharePoint sync in the background.
  //
  // Luke (FEG): the bundle at the top already accepted multiple files at
  // once, but this per-request upload only ever read files[0] — one file
  // per click. Loops over every selected file now, same as the bundle
  // does, uploading them sequentially so a batch of 10 doesn't fire 10
  // concurrent storage writes + DB inserts at once.
  const handleDdFileUpload = async (ddItemId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setDdUploading(prev => ({ ...prev, [ddItemId]: true }));
    setDdUploadSuccess(prev => ({ ...prev, [ddItemId]: null }));

    let anyFailed = false;
    for (const file of files) {
      try {
        // Reuse the seller-uploads path but keyed by dd_item_id for traceability
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const storagePath = `seller-uploads/${dealInfo.id}/dd/${ddItemId}/${fileName}`;

        const { error: storageErr } = await supabase.storage
          .from('deal-documents')
          .upload(storagePath, file, { cacheControl: '3600', upsert: false });
        if (storageErr) throw storageErr;

        const ddUploadResult = await recordSellerDdUpload(token, ddItemId, {
          name: fileName,
          originalName: file.name,
          path: storagePath,
          size: file.size,
          mimeType: file.type,
        });

        // SharePoint sync — non-blocking, fire & forget
        uploadToSharePointAsSeller(file, token, dealInfo.name, file.name, null)
          .catch(err => console.error('SharePoint DD upload failed (non-blocking):', err));

        // Fire AI categorization (non-blocking) — the file is already matched
        // to this DD item, so no need for ai-match-dd-item, but it still
        // needs a category_id + SharePoint category-folder placement, same
        // as the generic dropzone upload gets.
        if (ddUploadResult?.file_id) {
          const spUrl = import.meta.env.VITE_SUPABASE_URL;
          const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          // Extract PDF/XLSX text in the browser first (see
          // extractPdfExtraFields/extractXlsxExtraFields) — without this,
          // any real-text PDF over 4MB fails server-side as "too large
          // for vision" even though it isn't actually a scan, and large
          // XLSX files risk blowing the edge function's memory limit.
          const pdfExtra = await extractPdfExtraFields(file);
          const xlsxExtra = await extractXlsxExtraFields(file);
          fetch(`${spUrl}/functions/v1/auto-categorize-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify({ file_id: ddUploadResult.file_id, seller_token: token, ...pdfExtra, ...xlsxExtra }),
          })
            .then(r => r.json())
            .then(r => console.log('AI categorization:', r))
            .catch(err => console.error('AI categorization failed:', err));
        }
      } catch (err) {
        console.error('DD upload error:', file.name, err);
        anyFailed = true;
      }
    }

    setDdUploadSuccess(prev => ({ ...prev, [ddItemId]: !anyFailed }));
    setDdUploading(prev => ({ ...prev, [ddItemId]: false }));
    if (ddFileInputRefs.current[ddItemId]) {
      ddFileInputRefs.current[ddItemId].value = '';
    }

    setTimeout(() => {
      getSellerDdItems(token)
        .then(r => { if (r?.valid) setDdItems(r.items || []); })
        .catch(() => {});
    }, 800);
  };

  // Phase C.1 — open the mark-as-handled (or mark-N/A) modal for a specific item
  const handleOpenMarkHandled = (item, mode = 'handled') => {
    setMarkHandledItem(item);
    setMarkHandledMode(mode);
  };

  // Phase C.1 — submit the resolution note
  const handleConfirmMarkHandled = async (note) => {
    if (!markHandledItem) return;
    await recordSellerDdResolution(token, markHandledItem.id, note, markHandledMode === 'na');
    // Optimistic reload of DD items
    try {
      const r = await getSellerDdItems(token);
      if (r?.valid) setDdItems(r.items || []);
    } catch {}
    setMarkHandledItem(null);
  };

  const handleDownload = async (file) => {
    try {
      const url = await getFileUrl(file.storage_path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleSectionSaved = (category) => {
    // Refresh info request status after a section is saved
    getSellerInfoRequest(token)
      .then(result => {
        if (result) {
          setInfoRequest(result.info_request || null);
          setSubmittedData(result.submitted_data || null);
        }
      })
      .catch(() => {});
  };

  // Generic drag-and-drop upload handler (multi-file, no task matching)
  const handleGenericUpload = async (files) => {
    if (!files || files.length === 0 || !dealInfo) return;
    const dealId = dealInfo.id;
    const dealName = dealInfo.name;
    const spUrl = import.meta.env.VITE_SUPABASE_URL;
    const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    for (const file of files) {
      const fileKey = `gen-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      setGenericUploads(prev => [...prev, { key: fileKey, name: file.name, size: file.size, status: 'uploading' }]);

      try {
        // 1. Upload to Supabase Storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const storagePath = `seller-uploads/${dealId}/general/${fileName}`;

        const { error: storageErr } = await supabase.storage
          .from('deal-documents')
          .upload(storagePath, file, { cacheControl: '3600', upsert: false });
        if (storageErr) throw storageErr;

        // 2. Record in DB (no task_id)
        const { data: dbData, error: dbErr } = await supabase.rpc('record_seller_general_upload', {
          p_token: token,
          p_file_name: fileName,
          p_original_name: file.name,
          p_storage_path: storagePath,
          p_size_bytes: file.size,
          p_mime_type: file.type
        });
        if (dbErr) throw dbErr;
        if (!dbData?.success) throw new Error(dbData?.error || 'Upload failed');

        // 3. Sync to SharePoint "00 Seller Uploads" folder (non-blocking)
        try {
          const arrayBuffer = await file.arrayBuffer();
          const base64 = btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ''));
          fetch(`${spUrl}/functions/v1/sharepoint-sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify({
              action: 'upload-file',
              sellerToken: token,
              folderPath: `Acquisitions/${dealName}/00 Seller Uploads`,
              fileName: `${new Date().toISOString().slice(5,16).replace(/[-T:]/g, '')}_${file.name}`,
              fileContentBase64: base64,
              mimeType: file.type,
            }),
          }).catch(err => console.error('SharePoint sync failed:', err));
        } catch (spErr) { console.error('SharePoint sync failed:', spErr); }

        setGenericUploads(prev => prev.map(u => u.key === fileKey ? { ...u, status: 'done' } : u));

        // Fire AI auto-categorization (non-blocking). Extract PDF text in
        // the browser first — Supabase edge can't run pdfjs, so without
        // this any real-text PDF over 4MB (a normal bank statement, say)
        // fails as "too large for vision" even though it has perfectly
        // good extractable text.
        if (dbData?.file_id) {
          const pdfExtra = await extractPdfExtraFields(file);
          const xlsxExtra = await extractXlsxExtraFields(file);
          fetch(`${spUrl}/functions/v1/auto-categorize-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify({ file_id: dbData.file_id, seller_token: token, ...pdfExtra, ...xlsxExtra }),
          })
            .then(r => r.json())
            .then(r => console.log('AI categorization:', r))
            .catch(err => console.error('AI categorization failed:', err));

          // Phase C — also try to match the file to a DD checklist item.
          // Parallel to categorization; updates dd_item_id + item status
          // if confidence is high enough. UI refreshes via realtime.
          fetch(`${spUrl}/functions/v1/ai-match-dd-item`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify({ file_id: dbData.file_id, seller_token: token }),
          })
            .then(r => r.json())
            .then(r => {
              console.log('DD match:', r);
              // Refresh portal DD list if we matched something
              if (r?.matched) {
                setTimeout(() => {
                  getSellerDdItems(token)
                    .then(res => { if (res?.valid) setDdItems(res.items || []); })
                    .catch(() => {});
                }, 600);
              }
            })
            .catch(err => console.error('DD match failed:', err));
        }
      } catch (err) {
        console.error('Generic upload error:', err);
        setGenericUploads(prev => prev.map(u => u.key === fileKey ? { ...u, status: 'error', error: err.message } : u));
      }
    }
  };

  const neededTasks = tasks.filter(t => t.status === 'Requested');
  const receivedTasks = tasks.filter(t => ['Received', 'Under Review'].includes(t.status));
  const completedTasks = tasks.filter(t => t.status === 'Complete');
  const availableTasks = tasks.filter(t => t.status === 'Not Started');
  // Third-party scopes (and seller, for their own items) have no legacy
  // tasks — fold DD-checklist items into the header count so it's accurate.
  const ddNeeded = ddItems.filter(i => i.assigned_to === scope && (i.status === 'not_started' || i.status === 'requested')).length;
  const pendingCount = neededTasks.length + ddNeeded;

  const handleRequestRenewCode = async (e) => {
    e?.preventDefault?.();
    if (!renewEmail.trim()) return;
    setRenewSending(true);
    setRenewError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-seller-portal-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ expired_token: token, email: renewEmail.trim() }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Could not send a code');
      setRenewStep('code');
    } catch (err) {
      setRenewError(err.message);
    } finally {
      setRenewSending(false);
    }
  };

  const handleVerifyRenewCode = async (e) => {
    e?.preventDefault?.();
    if (!renewCode.trim()) return;
    setRenewSending(true);
    setRenewError(null);
    try {
      const { data: result, error: rpcErr } = await supabase.rpc('verify_seller_portal_otp', {
        p_expired_token: token,
        p_email: renewEmail.trim(),
        p_code: renewCode.trim(),
      });
      if (rpcErr) throw rpcErr;
      if (!result.success) throw new Error(result.error || 'Verification failed');
      // Reload straight into the new token — same URL pattern App.jsx
      // already parses on mount (/seller/<token>).
      window.location.href = `/seller/${result.token}`;
    } catch (err) {
      setRenewError(err.message);
    } finally {
      setRenewSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: displayPrimary }} />
          <p className="text-gray-500">Loading secure upload portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-sm w-full">
          <AlertCircle className="w-14 h-14 mx-auto mb-4 text-amber-400" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">This link has expired</h1>
          <p className="text-gray-500 text-sm mb-6">{error}</p>

          {renewStep === 'email' ? (
            <form onSubmit={handleRequestRenewCode} className="text-left">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Enter the email this link was sent to, and we'll email you a code to get back in.
              </label>
              <input
                type="email"
                required
                value={renewEmail}
                onChange={e => setRenewEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
              {renewError && <p className="text-xs text-red-600 mb-3">{renewError}</p>}
              <button
                type="submit"
                disabled={renewSending}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: displayPrimary }}
              >
                {renewSending && <Loader2 className="w-4 h-4 animate-spin" />}
                Send me a code
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyRenewCode} className="text-left">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Enter the 6-digit code we just emailed to {renewEmail}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={renewCode}
                onChange={e => setRenewCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full border rounded-lg px-3 py-2 text-sm mb-3 tracking-[0.3em] text-center text-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
              {renewError && <p className="text-xs text-red-600 mb-3">{renewError}</p>}
              <button
                type="submit"
                disabled={renewSending}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: displayPrimary }}
              >
                {renewSending && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify & continue
              </button>
              <button
                type="button"
                onClick={() => { setRenewStep('email'); setRenewCode(''); setRenewError(null); }}
                className="w-full text-center text-xs text-gray-400 mt-3 hover:text-gray-600"
              >
                Use a different email or send a new code
              </button>
            </form>
          )}

          <p className="text-xs text-gray-400 mt-6">
            Still stuck? Contact the team that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  if (showTos && !tosAccepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4" style={{ color: displayPrimary }}>Terms of Use</h2>
          <div className="prose prose-sm text-gray-600 mb-6">
            <p>By uploading documents or providing information through this portal, you agree to the following:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>You have the legal authority to share these documents and information.</li>
              <li>The documents and information you provide are accurate and authentic.</li>
              <li>Everything you submit will be used solely for the purpose of evaluating this transaction.</li>
              <li>Your uploads are encrypted and transmitted securely.</li>
            </ul>
          </div>
          <div className="flex gap-3 justify-end">
            <button 
              onClick={() => setShowTos(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={() => { setTosAccepted(true); setShowTos(false); }}
              className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              I Accept
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {!tosAccepted ? (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
              <Shield className="w-8 h-8" style={{ color: displayPrimary }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: displayPrimary }}>
              Secure Document Portal
            </h1>
            <p className="text-gray-600 mb-6">
              You've been invited to upload documents for <strong>{dealInfo?.name}</strong>.
            </p>
            <button
              onClick={() => setShowTos(true)}
              className="w-full py-3 text-white rounded-lg font-medium hover:opacity-90 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Continue to Portal
            </button>
            <p className="text-xs text-gray-400 mt-4">
              By continuing, you agree to our terms of use
            </p>
          </div>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={orgConfig?.logo_url || LFG_LOGO_URL} 
                alt="Logo" 
                className="w-10 h-10 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <div className="text-xs" style={{ color: displaySecondary }}>Document Portal</div>
                <div className="font-bold" style={{ color: displayPrimary }}>
                  {dealInfo?.name}
                </div>
              </div>
            </div>
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span><strong>{pendingCount}</strong> needed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scope banner — only shown for third-party (non-seller) tokens */}
      {scope !== 'seller' && (
        <div className="px-4 pt-4">
          <div
            className="rounded-lg border px-4 py-3 flex items-start gap-3"
            style={{
              backgroundColor: `${primaryColor}08`,
              borderColor: `${primaryColor}33`,
            }}
          >
            <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: displayPrimary }} />
            <div className="text-sm">
              <div className="font-medium" style={{ color: displayPrimary }}>
                {scope === 'accountant' ? 'Accountant / CPA access'
                  : scope === 'broker' ? 'Broker / Representative access'
                  : scope === 'counsel' ? 'Seller Counsel access'
                  : scope === 'hr_survey' ? 'HR Survey'
                  : 'Limited access'}
                {scopeName ? ` — ${scopeName}` : ''}
              </div>
              <div className="text-gray-600 mt-0.5">
                {scope === 'hr_survey' ? (
                  <>This link opens the HR Survey for <strong>{dealInfo?.name}</strong>. Answer what you can — your responses save as you go.</>
                ) : (
                  <>You're uploading documents on behalf of the seller for <strong>{dealInfo?.name}</strong>. You'll only see documents specifically requested from you.</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase U.2 — hr_survey scope: render JUST the HR Survey form.
          No upload zone, no tasks, no DD checklist, no other BI tabs. */}
      {scope === 'hr_survey' && (
        <div className="px-4 py-6 max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <InfoFormSection
              token={token}
              infoRequest={infoRequest}
              submittedData={submittedData}
              onSectionSaved={handleSectionSaved}
              primaryColor={primaryColor}
              restrictToCategory="personnel_survey"
            />
          </div>
        </div>
      )}

      {/* Content (seller + other scoped) */}
      {scope !== 'hr_survey' && (
      <div className="px-4 py-6">

        {/* Drag-and-drop upload zone — seller only. Third-party scopes get a
            compact version below the checklist instead, so the checklist
            (the thing they actually need to act on) is what they see first. */}
        {scope === 'seller' && (
        <div className="mb-6">
          <div
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); handleGenericUpload(Array.from(e.dataTransfer.files)); }}
            onClick={() => genericInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-blue-400 bg-blue-50 scale-[1.01]'
                : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
            }`}
          >
            <input
              ref={genericInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt,.zip,.ppt,.pptx"
              onChange={e => { handleGenericUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
            />
            <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
            <p className="font-medium text-gray-700">
              {dragActive ? 'Drop files to upload' : 'Drop files here or click to browse'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Upload any documents — PDFs, spreadsheets, images, contracts, reports, etc.
            </p>
            <p className="text-xs text-gray-300 mt-2">
              We'll organize everything on our end
            </p>
          </div>

          {/* Generic upload results */}
          {genericUploads.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="text-xs font-medium text-gray-500 px-1">
                Uploaded Files ({genericUploads.filter(u => u.status === 'done').length}/{genericUploads.length})
              </div>
              {genericUploads.map(u => (
                <div key={u.key} className="flex items-center gap-3 bg-white rounded-lg border px-3 py-2">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 text-sm truncate text-gray-700">{u.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{(u.size / 1024).toFixed(0)} KB</span>
                  {u.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: displayPrimary }} />}
                  {u.status === 'done' && <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: displayGreen }} />}
                  {u.status === 'error' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" title={u.error} />}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Main layout — Business Info on left (seller only), docs on right */}
        <div className="flex gap-6 items-start">

            {/* LEFT 2/3 — Business Information (seller scope only) */}
            {scope === 'seller' && (
            <div className="flex-[2] min-w-0">
              {/* Collapsible header */}
              <button
                onClick={() => setInfoExpanded(!infoExpanded)}
                className="w-full flex items-center justify-between bg-white rounded-t-xl border-x border-t px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" style={{ color: displayPrimary }} />
                  <span className="font-semibold text-sm text-gray-800">Business Information</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    infoRequest?.status === 'complete' ? 'bg-green-100 text-green-700' :
                    infoRequest?.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {infoRequest?.status === 'complete' ? 'Complete' :
                     infoRequest?.status === 'partial'
                       ? `${(infoRequest?.categories_submitted || []).length} of 8 sections`
                       : 'Not started'}
                  </span>
                </div>
                {infoExpanded
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                }
              </button>
              {infoExpanded ? (
                <InfoFormSection
                  token={token}
                  infoRequest={infoRequest}
                  submittedData={submittedData}
                  onSectionSaved={handleSectionSaved}
                  primaryColor={primaryColor}
                />
              ) : (
                <div className="bg-white rounded-b-xl border px-4 py-2 text-xs text-gray-400">
                  Click to expand and fill out business details
                </div>
              )}
            </div>
            )}

            {/* RIGHT — docs panel. Takes full width for scoped (third-party) tokens since Business Info is hidden. */}
            <div className={`${scope === 'seller' ? 'flex-[1]' : 'flex-1'} min-w-0 space-y-4`}>

              {/* Phase C — DD checklist panel.
                  Third-party scopes see ONLY this; seller scope sees it above the task-based panels. */}
              {ddItems.length > 0 && (
                <DdChecklistPanel
                  items={ddItems}
                  scope={scope}
                  uploading={ddUploading}
                  uploadSuccess={ddUploadSuccess}
                  fileInputRefs={ddFileInputRefs}
                  handleUpload={handleDdFileUpload}
                  handleMarkHandled={handleOpenMarkHandled}
                  primaryColor={primaryColor}
                  displayPrimary={displayPrimary}
                  displayNavy={displayNavy}
                  displayGreen={displayGreen}
                />
              )}

              {/* Task-based panels — hidden for third-party scopes (DD flow replaces them) */}
              {scope === 'seller' && (
              <>
              {/* Phase Q.4 — Your team. Seller can invite counsel,
                  accountant, broker to upload on the seller's behalf. */}
              <YourTeamPanel
                sellerToken={token}
                primaryColor={primaryColor}
                displayNavy={displayNavy}
              />

              {/* Documents Needed */}
              {neededTasks.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: displayNavy }}>
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    Documents Needed ({neededTasks.length})
                  </h2>
                  <div className="space-y-2">
                    {neededTasks.map(task => (
                      <TaskCard key={task.id} task={task} uploading={uploading} uploadSuccess={uploadSuccess}
                        fileInputRefs={fileInputRefs} handleFileUpload={handleFileUpload}
                        handleDownload={handleDownload} primaryColor={primaryColor} compact={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* Available to Upload */}
              {availableTasks.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-1 flex items-center gap-2" style={{ color: displayNavy }}>
                    <FileText className="w-4 h-4 text-gray-500" />
                    Available to Upload ({availableTasks.length})
                  </h2>
                  <p className="text-xs text-gray-400 mb-2">Upload these now to speed up the process</p>
                  <div className="space-y-2">
                    {availableTasks.map(task => (
                      <TaskCard key={task.id} task={task} uploading={uploading} uploadSuccess={uploadSuccess}
                        fileInputRefs={fileInputRefs} handleFileUpload={handleFileUpload}
                        handleDownload={handleDownload} primaryColor={primaryColor} compact={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* Files Received */}
              {receivedTasks.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: displayNavy }}>
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    Files Received ({receivedTasks.length})
                  </h2>
                  <div className="space-y-2">
                    {receivedTasks.map(task => (
                      <TaskCard key={task.id} task={task} uploading={uploading} uploadSuccess={uploadSuccess}
                        fileInputRefs={fileInputRefs} handleFileUpload={handleFileUpload}
                        handleDownload={handleDownload} primaryColor={primaryColor} compact={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed */}
              {completedTasks.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: displayNavy }}>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Completed ({completedTasks.length})
                  </h2>
                  <div className="bg-white rounded-lg border divide-y">
                    {completedTasks.map(task => (
                      <div key={task.id} className="px-3 py-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 flex-1 truncate">{task.name}</span>
                        {task.files?.length > 0 && (
                          <span className="text-xs text-gray-400">{task.files.length} file{task.files.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tasks.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
                  <CheckSquare className="w-10 h-10 mx-auto mb-2" style={{ color: displayGreen }} />
                  <div className="text-sm font-medium text-gray-700">All caught up!</div>
                  <div className="text-xs text-gray-500 mt-1">No documents currently requested.</div>
                </div>
              )}
              </>
              )}

              {/* Third-party scope with no DD items yet — empty state */}
              {scope !== 'seller' && ddItems.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
                  <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <div className="text-sm font-medium text-gray-700">Nothing to upload yet</div>
                  <div className="text-xs text-gray-500 mt-1">
                    The LFG team hasn't sent you a diligence request yet. If you have documents to share in the meantime, use the upload option below.
                  </div>
                </div>
              )}

              {/* Compact "something else" uploader — third-party scopes only.
                  Deliberately small and placed after the checklist so it doesn't
                  compete with it for attention; the checklist is what they came for. */}
              {scope !== 'seller' && (
                <div>
                  <div
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                    onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                    onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); handleGenericUpload(Array.from(e.dataTransfer.files)); }}
                    onClick={() => genericInputRef.current?.click()}
                    className={`border border-dashed rounded-lg px-3 py-2.5 text-center cursor-pointer transition-colors text-xs ${
                      dragActive ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 hover:border-gray-400 bg-white text-gray-400 hover:text-gray-500'
                    }`}
                  >
                    <input
                      ref={genericInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt,.zip,.ppt,.pptx"
                      onChange={e => { handleGenericUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
                    />
                    Have something else that isn't on the checklist? <span className="underline">Drop it here</span>.
                  </div>
                  {genericUploads.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {genericUploads.map(u => (
                        <div key={u.key} className="flex items-center gap-2 bg-white rounded border px-2.5 py-1.5 text-xs">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="flex-1 truncate text-gray-700">{u.name}</span>
                          {u.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" style={{ color: displayPrimary }} />}
                          {u.status === 'done' && <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: displayGreen }} />}
                          {u.status === 'error' && <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" title={u.error} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>


        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-400 flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          Your documents and information are encrypted and transmitted securely
        </div>
      </div>
      )}{/* end scope !== 'hr_survey' */}
      </>
      )}

      {/* Mark-as-handled modal (Phase C.1) */}
      {markHandledItem && (
        <MarkHandledModal
          item={markHandledItem}
          mode={markHandledMode}
          onCancel={() => setMarkHandledItem(null)}
          onConfirm={handleConfirmMarkHandled}
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
}

// ===========================================================================
// YourTeamPanel — Phase Q.4. Seller can invite their counsel / accountant /
// broker to upload documents on the seller's behalf. Each role gets its
// own scoped portal token; re-inviting the same role overwrites the
// previous link (which intentionally invalidates it).
// ===========================================================================
function YourTeamPanel({ sellerToken, primaryColor, displayNavy }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('counsel');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastIssued, setLastIssued] = useState(null);  // { scope, portal_url, email }

  const refresh = async () => {
    try {
      const list = await sellerListThirdPartyInvites(sellerToken);
      setInvites(list);
    } catch (e) {
      console.warn('Failed to load third-party invites:', e?.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [sellerToken]);

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!email.trim()) { setError('Email is required'); return; }
    setSubmitting(true);
    let emailSent = false;
    try {
      const result = await sellerInviteThirdParty(sellerToken, scope, email.trim(), name.trim());

      // Auto-send the invite email so the seller doesn't have to copy-paste.
      // Falls through silently if the email send fails — the seller still
      // sees the URL they can share manually.
      try {
        const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-seller-team-invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            seller_token: sellerToken,
            scope,
            email: email.trim(),
            name: name.trim() || null,
            portal_url: result.portal_url,
          }),
        });
        emailSent = r.ok;
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          console.warn('Email send failed:', err);
        }
      } catch (mailErr) {
        console.warn('Email send threw:', mailErr?.message);
      }

      setLastIssued({ scope, portal_url: result.portal_url, email: email.trim(), emailSent });
      setEmail(''); setName('');
      await refresh();
    } catch (e) {
      setError(e?.message || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const SCOPE_LABEL = { counsel: 'Counsel', accountant: 'Accountant', broker: 'Broker' };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: displayNavy }}>
          <Users className="w-4 h-4" />
          Your team
        </h2>
        <span className="text-[11px]" style={{ color: '#94A3B8' }}>
          Give your counsel, accountant, or broker their own upload link
        </span>
      </div>

      {/* Existing invites */}
      {!loading && invites.length > 0 && (
        <div className="mb-3 space-y-1">
          {invites.map(inv => (
            <div key={inv.scope} className="flex items-center justify-between text-[12px] px-2.5 py-1.5 rounded"
                 style={{ backgroundColor: '#FAFBFD', border: '1px solid #F0F2F6' }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}>
                  {SCOPE_LABEL[inv.scope] || inv.scope}
                </span>
                <span className="truncate" style={{ color: '#0F172A' }}>{inv.scope_name || inv.scope_email}</span>
                <span className="text-[11px] truncate" style={{ color: '#64748B' }}>{inv.scope_email}</span>
              </div>
              <span className="text-[11px] flex-shrink-0" style={{ color: '#94A3B8' }}>
                Active
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Issued banner */}
      {lastIssued && (
        <div className="mb-3 rounded-lg p-3 text-[12px]" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
          {lastIssued.emailSent ? (
            <>
              ✓ Emailed {SCOPE_LABEL[lastIssued.scope]} access to <strong>{lastIssued.email}</strong>.
              They should receive it within a minute. If they don't see it, share this link directly:
            </>
          ) : (
            <>
              ✓ Created {SCOPE_LABEL[lastIssued.scope]} access for <strong>{lastIssued.email}</strong>.
              We weren't able to auto-email — share this link with them directly:
            </>
          )}
          <div className="mt-1.5 px-2 py-1 rounded font-mono text-[11px] break-all" style={{ backgroundColor: '#fff', border: '1px solid #BBF7D0' }}>
            {lastIssued.portal_url}
          </div>
        </div>
      )}

      {/* Invite form */}
      <form onSubmit={handleInvite} className="grid grid-cols-12 gap-2">
        <select
          value={scope}
          onChange={e => setScope(e.target.value)}
          className="col-span-3 h-8 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
          style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
        >
          <option value="counsel">Counsel</option>
          <option value="accountant">Accountant</option>
          <option value="broker">Broker</option>
        </select>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Name (optional)"
          className="col-span-3 h-8 text-[12px] rounded-md px-2 focus:outline-none"
          style={{ border: '1px solid #E5E9F0', backgroundColor: 'white' }}
        />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="email@firm.com"
          className="col-span-4 h-8 text-[12px] rounded-md px-2 focus:outline-none"
          style={{ border: '1px solid #E5E9F0', backgroundColor: 'white' }}
          required
        />
        <button
          type="submit"
          disabled={submitting}
          className="col-span-2 h-8 px-3 text-[12px] font-semibold rounded-md text-white hover:opacity-90 cursor-pointer disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {submitting ? '…' : 'Invite'}
        </button>
      </form>
      {error && (
        <div className="mt-2 text-[12px]" style={{ color: '#B91C1C' }}>{error}</div>
      )}
      <p className="text-[11px] mt-2" style={{ color: '#94A3B8' }}>
        Re-inviting the same role replaces the previous link for that role.
      </p>
    </div>
  );
}

// ===========================================================================
// MarkHandledModal — portal user marks an item complete without uploading,
// with a required note (e.g. "Granted QBO access to abc@cohenco.com").
// ===========================================================================
function MarkHandledModal({ item, mode = 'handled', onCancel, onConfirm, primaryColor }) {
  const isNa = mode === 'na';
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!isNa && !note.trim()) { setError('Please tell us how it was handled'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(note.trim());
    } catch (err) {
      setError(err.message || 'Could not save');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b">
          <h3 className="font-semibold text-sm">{isNa ? "Mark as not applicable" : 'Mark as handled without an upload'}</h3>
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.request_text}</p>
        </div>
        <div className="px-5 py-4">
          <label className="text-xs font-semibold text-gray-600 block mb-1">
            {isNa ? 'Why doesn\'t this apply? (optional)' : 'How was this handled?'}
          </label>
          <textarea
            value={note}
            onChange={e => { setNote(e.target.value); setError(null); }}
            placeholder={isNa
              ? "e.g. We don't operate a crematory"
              : 'e.g. Granted QBO admin access to kgoessler@cohenco.com and abattaglia@cohenco.com'}
            rows={4}
            className="w-full text-sm border rounded px-2 py-1.5 focus:outline-none focus:ring-1"
            disabled={submitting}
          />
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          <p className="text-xs text-gray-400 mt-1">
            {isNa
              ? "This is stored with the item so the LFG team knows why it's marked N/A."
              : 'This note is stored with the item so the LFG team knows what happened.'}
          </p>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={submitting}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting || (!isNa && !note.trim())}
                  className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: isNa ? '#64748B' : primaryColor }}>
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
            {isNa ? 'Mark N/A' : 'Mark as handled'}
          </button>
        </div>
      </div>
    </div>
  );
}
